import { Button, Col, Flex, Form, Popover, Switch } from "antd";
import { useEffect, useRef, useState } from "react";
import { Guess } from "../types/Guess";
import EditGuessModal from "./guess/EditGuessModal";
import { RankingDisplay } from "./RankingDisplay";
import { ConfigurationForm } from "./ConfigurationForm";
import { NewGuessForm } from "./NewGuessForm";
import { WinnerList } from "./WinnerList";
import { SettingOutlined } from '@ant-design/icons';


function Lite() {

    // determine mobile mode
    const [width, setWidth] = useState<number>(window.innerWidth);
    function handleWindowSizeChange() {
        setWidth(window.innerWidth);
    }
    useEffect(() => {
        window.addEventListener('resize', handleWindowSizeChange);
        return () => {
            window.removeEventListener('resize', handleWindowSizeChange);
        }
    }, []);
    const isMobile = width <= 768;

    const [values, setValues] = useState<number[]>([100, 200, 300, 400]);
    const [rankAmount, setRankAmount] = useState<number>(3);

    const [guesses, setGuesses] = useState<Guess[]>([]);

    const [editingGuess, setEditingGuess] = useState<Guess>();
    const [editOpen, setEditOpen] = useState(false);

    const [lowerBound, setLowerBound] = useState(0);
    const [upperBound, setUpperBound] = useState(0);

    const [showWinnerPositions, setShowWinnerPostions] = useState<boolean>(false);


    const addGuess = (name: string, val: number) => {
        guesses.push({
            name,
            value: val,
            distance: Math.round(Math.min(...values.map(v => Math.abs(v - val))) * 100) / 100
        })
        sortGuesses(guesses);
    }

    const onEdit = (item: Guess) => {
        setEditingGuess(item);
        setEditOpen(true);
    }

    const editGuess = (guess: Guess, guessName: string, guessVal: number) => {
        const savedGuess = guesses.find(g => g === guess);
        if (savedGuess) {
            savedGuess.name = guessName;
            savedGuess.value = guessVal;
            savedGuess.distance = Math.round(Math.min(...values.map(v => Math.abs(v - guessVal))) * 100) / 100;
        }
        sortGuesses(guesses);
    }

    const onDelete = (guess: Guess) => {
        const index = guesses.indexOf(guess, 0);
        if (index > -1) {
            guesses.splice(index, 1);
        }
        sortGuesses(guesses);
    }

    const nameInputRef = useRef<any>(null);

    const sortGuesses = (guessesToSort: Guess[]) => {
        const sorted = guessesToSort.sort((a, b) => a.distance - b.distance);
        computeBounds(sorted, rankAmount);
        setGuesses([...sorted]);
    }

    const computeDistances = (newValues: number[]) => {
        const computedGuesses = guesses.map(g => (
            {
                ...g,
                distance: Math.round(Math.min(...newValues.map(v => Math.abs(v - g.value))) * 100) / 100
            }
        ));
        sortGuesses(computedGuesses);
    }

    const computeBounds = (newGuesses: Guess[], newRankAmount: number) => {
        if (newGuesses.length >= newRankAmount) {
            const lastInRange = newGuesses[newRankAmount - 1];
            setLowerBound(Math.round((Math.min(...values) - lastInRange.distance) * 100) / 100);
            setUpperBound(Math.round((Math.max(...values) + lastInRange.distance) * 100) / 100);
        }
        else {
            setLowerBound(0);
            setUpperBound(0);
        }
    }

    return (
        <>
            <EditGuessModal
                guess={editingGuess}
                open={editOpen}
                setOpen={(val: boolean) => {
                    setEditOpen(val);
                    setEditingGuess(undefined)
                }}
                editGuess={editGuess} />


            <>
                {isMobile ?
                    (

                        <Flex className="configStack">
                            <ConfigurationForm
                                values={values}
                                setValues={setValues}
                                computeDistances={computeDistances}
                                computeBounds={computeBounds}
                                setRankAmount={setRankAmount}
                                rankAmount={rankAmount}
                                guesses={guesses} />

                            <RankingDisplay
                                lowerBound={lowerBound}
                                upperBound={upperBound}
                                rankAmount={rankAmount}
                                values={values}
                                guesses={guesses}
                                showWinnerPositions={showWinnerPositions}
                                setShowWinnerPositions={setShowWinnerPostions}
                                isMobile={isMobile}
                            />


                            <NewGuessForm
                                nameInputRef={nameInputRef}
                                addGuess={addGuess} />

                            <WinnerList
                                guesses={guesses}
                                rankAmount={rankAmount}
                                onEdit={onEdit}
                                onDelete={onDelete} />


                        </Flex>
                    )
                    :
                    (
                        <Flex vertical={true} className="grow" style={{ overflow: 'auto' }}>
                            <Flex className="configHeader">
                                <Col span={6}>
                                    <ConfigurationForm
                                        values={values}
                                        setValues={setValues}
                                        computeDistances={computeDistances}
                                        computeBounds={computeBounds}
                                        setRankAmount={setRankAmount}
                                        rankAmount={rankAmount}
                                        guesses={guesses} />
                                </Col>
                                <Col span={1}></Col>
                                <Col span={16} className="rankingDisplay">
                                    <RankingDisplay
                                        lowerBound={lowerBound}
                                        upperBound={upperBound}
                                        rankAmount={rankAmount}
                                        values={values}
                                        guesses={guesses}
                                        setShowWinnerPositions={setShowWinnerPostions}
                                        showWinnerPositions={showWinnerPositions}
                                        isMobile={isMobile}
                                    />
                                </Col>
                                <Col span={1}>
                                    <Popover className="settings" content={
                                        <Form>
                                            <Popover placement="left" content={<div className="infoPopup">Sollten die Positionen der Gewinner im Balken angezeigt werden?</div>}>
                                                <Form.Item label="Gewinnerpositionen">
                                                    <Switch onChange={setShowWinnerPostions} value={showWinnerPositions}></Switch>
                                                </Form.Item>
                                            </Popover>
                                        </Form>

                                    } trigger='click' placement="leftTop" title='Einstellungen'>
                                        <Button icon={<SettingOutlined />} size="large" />
                                    </Popover>
                                </Col>
                            </Flex >
                            <Flex className="grow" style={{ overflow: 'auto' }}>
                                <Col span={6}>
                                    <NewGuessForm
                                        nameInputRef={nameInputRef}
                                        addGuess={addGuess} />
                                </Col>
                                <Col span={1}></Col>
                                <Col span={17} style={{ overflow: 'auto', display: 'flex', flexGrow: 1 }}>
                                    <WinnerList
                                        guesses={guesses}
                                        rankAmount={rankAmount}
                                        onEdit={onEdit}
                                        onDelete={onDelete} />
                                </Col>
                            </Flex>
                        </Flex >
                    )
                }
            </>
        </>
    );
}

export default Lite;